import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { navigate, registerNavigationGuard } from "../router.tsx";
import { useReloadHold } from "../shell/build-refresh.ts";
import { useUnsavedWork } from "../shell/session-ended.ts";

export function useReviewGuard(dirty: boolean, text: string) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<(() => void) | null>(null);
  const bypass = useRef(false);
  useReloadHold(dirty);
  useUnsavedWork(dirty, text);
  useEffect(() => {
    if (!dirty) {
      return;
    }
    const href = window.location.pathname + window.location.search;
    const unregister = registerNavigationGuard((proceed) => {
      if (bypass.current) {
        return true;
      }
      setPending(() => proceed);
      return false;
    });
    const unload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const back = (e: PopStateEvent) => {
      if (bypass.current) {
        return;
      }
      const target = window.location.pathname + window.location.search;
      e.stopImmediatePropagation();
      window.history.pushState(null, "", href);
      setPending(() => () => navigate(target, { replace: true }));
    };
    window.addEventListener("beforeunload", unload);
    window.addEventListener("popstate", back, true);
    return () => {
      unregister();
      window.removeEventListener("beforeunload", unload);
      window.removeEventListener("popstate", back, true);
    };
  }, [dirty]);
  return {
    confirm: (action: () => void) => {
      if (dirty) {
        setPending(() => action);
      } else {
        action();
      }
    },
    dialog: (
      <AppDialog
        open={!!pending}
        onClose={() => setPending(null)}
        title={t("wiki.changes.unsaved")}
      >
        <DialogFooter>
          <Button variant="outline" onClick={() => setPending(null)}>
            {t("wiki.changes.keepEditing")}
          </Button>
          <Button
            onClick={() => {
              const action = pending;
              setPending(null);
              bypass.current = true;
              action?.();
              queueMicrotask(() => {
                bypass.current = false;
              });
            }}
          >
            {t("wiki.changes.discardEdits")}
          </Button>
        </DialogFooter>
      </AppDialog>
    ),
  };
}
